import { add } from 'date-fns'

import { HomesFilters, JobsFilters, PropertiesFilters, ReservationsFilters } from '@/context'
import { HomesFilterParams } from '@/types/Homes'
import { JobsFilterParams } from '@/types/Job'
import { PropertiesFilterParams } from '@/types/Property'
import { ReservationsFilterParams } from '@/types/Reservation'

import { formatDate } from '.'

export const getReservationFilterParams = (filters: ReservationsFilters): Partial<ReservationsFilterParams> => {
  const today = formatDate(new Date(), 'yyyy-MM-dd')
  const twoDays = formatDate(add(new Date(), { hours: 48 }), 'yyyy-MM-dd')
  const threeDays = formatDate(add(new Date(), { days: 3 }), 'yyyy-MM-dd')
  const sevenDays = formatDate(add(new Date(), { days: 7 }), 'yyyy-MM-dd')
  return Object.keys(filters).reduce((prev, key) => {
    const value = filters[key]

    if (value) {
      switch (key) {
        case 'autopilot':
          return { ...prev, autopilot: true }
        case 'moveInToday':
          return { moveInDate: today }
        case 'moveIn48Hrs':
          return { moveInDate: today, moveInMax: twoDays }
        case 'moveIn3days':
          return { moveInDate: today, moveInMax: threeDays }
        case 'moveIn7Days':
          return { moveInDate: today, moveInMax: sevenDays }
        case 'currentReservations':
          return { reservationStatus: 'current' }
        case 'flightxInProgress':
          return { flightxInProgress: true }
        case 'lexit':
          return { lexit: true }
        case 'approved':
          return { approved: true }
        case 'pending':
          return { pending: true, approved: false, moveInDate: today, moveInMax: threeDays }
        default:
          return prev
      }
    }
    return prev
  }, {})
}

export const getPropertiesFilterParams = (filters: PropertiesFilters): Partial<PropertiesFilterParams> => {
  return Object.keys(filters).reduce((prev, key) => {
    const value = filters[key]
    if (value) {
      switch (key) {
        case 'blocked':
          return { blocked: true }
        default:
          return prev
      }
    }
    return prev
  }, {})
}

export const getHomesFilterParams = (filters: HomesFilters): Partial<HomesFilterParams> => {
  return Object.keys(filters)
    .sort()
    .reduce((prev, key: keyof HomesFilters): Partial<HomesFilterParams> => {
      const value = filters[key]
      if (value) {
        switch (key) {
          case 'autopilot':
            return { ...prev, autopilot: true }
          case 'blocked':
            return { ...prev, blocked: true }
          case 'vacant':
            return { ...prev, vacant: true }
          case 'newOD':
            return { ...prev, newOD: true }
          case 'direct':
            return { ...prev, direct: true }
          case 'leased':
            return { ...prev, leased: true }
          case 'standard':
            if (prev['direct']) {
              return { ...prev, direct: undefined }
            }
            return { ...prev, direct: false }
          case 'published':
            return { ...prev, published: true }
          case 'installed':
            return { ...prev, installed: true }
          case 'occupied':
            return { ...prev, occupied: true }
          case 'scheduledForDeinstall':
            return { ...prev, scheduledForDeinstall: true }
          default:
            return prev
        }
      }
      return prev
    }, {})
}

export const getJobsFilterParams = (filters: JobsFilters): JobsFilterParams => {
  const today = formatDate(new Date(), 'yyyy-MM-dd') as string
  const sevenDays = formatDate(add(new Date(), { days: 7 }), 'yyyy-MM-dd') as string
  return Object.keys(filters).reduce(
    (prev, key: keyof JobsFilters): JobsFilterParams => {
      const value = filters[key]
      if (value) {
        switch (key) {
          case 'cancelled':
            return { ...prev, cancelled: true }
          case 'claimed':
            return { ...prev, claimed: true }
          case 'unclaimed':
            return { ...prev, claimed: false }
          case 'completed':
            return { ...prev, completed: true }
          case 'future':
            return { ...prev, future: true }
          case 'next7Days':
            return { ...prev, scheduleDateMin: today, scheduleDateMax: sevenDays }
          default:
            return prev
        }
      }
      return prev
    },
    { cancelled: false } as JobsFilterParams, // default to filter out cancelled job
  )
}
